/**
 * ConnectionOutputHandle - Reusable output handle component
 *
 * Renders the output (source) handle for both FlowScreenNode and FlowBlockNode.
 * Uses shared node-handle CSS classes and constants from handleConfig.
 *
 * Sprint: sprint-8
 * Pattern: Handle component reuse across entity types
 */

import React from 'react';
import { Handle, Position } from '@xyflow/react';
import { useTheme } from '@mui/material/styles';
import {
  HANDLE_DIMENSIONS,
  HANDLE_OFFSETS,
  HANDLE_TEXT,
  HANDLE_BORDER_RADIUS,
} from '@modules/reactFlow/constants/handleConfig';
import type { ConnectionHandle } from '@modules/reactFlow/types/flow-canvas-types';
import { TransitionIdGenerator } from '@modules/reactFlow/utils/converters/TransitionIdGenerator';

export interface ConnectionOutputHandleProps {
  entityId: string;
  handle: ConnectionHandle;
  index: number;
}

/**
 * ConnectionOutputHandle - Output handle for entity nodes
 *
 * Renders the right-side output handle with:
 * - Theme-based styling
 * - CSS classes for hover effects
 * - Data attributes for testing and functionality
 * - Dynamic positioning based on handle.position.y
 * - Label text with ellipsis for long labels
 */
export const ConnectionOutputHandle: React.FC<ConnectionOutputHandleProps> = ({
  entityId,
  handle,
  index,
}) => {
  const theme = useTheme();

  return (
    <Handle
      key={handle.id}
      type="source"
      position={Position.Right}
      id={handle.id}
      className="node-custom-handle node-custom-handle-source"
      data-testid={`handle-output-${entityId}-${handle.sourceActionKey || handle.id}`}
      data-handle-type="source"
      data-handle-position="right"
      data-action-key={handle.sourceActionKey || handle.id}
      data-handle-label={typeof handle.label === 'string' ? handle.label : `Btn ${index + 1}`}
      data-handlepos="right"
      style={{
        right: `${HANDLE_OFFSETS.OUTPUT_RIGHT}px`,
        top: `${handle.position.y}px`,
        width: `${HANDLE_DIMENSIONS.OUTPUT_WIDTH}px`,
        height: `${HANDLE_DIMENSIONS.HEIGHT}px`,
        borderRadius: `${HANDLE_BORDER_RADIUS}px`,
        background: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
      }}
      title={typeof handle.label === 'string' ? handle.label : `${handle.elementType || 'Button'}`}
    >
      <span
        style={{
          fontSize: HANDLE_TEXT.OUTPUT_FONT_SIZE,
          lineHeight: '1',
          textAlign: 'center',
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          maxWidth: `${HANDLE_DIMENSIONS.OUTPUT_WIDTH - 4}px`,
        }}
      >
        {typeof handle.label === 'string' ? handle.label : `${HANDLE_TEXT.DEFAULT_BTN_PREFIX} ${index + 1}`}
      </span>
    </Handle>
  );
};

/**
 * DefaultOutputHandle - Default handle when no handles provided
 *
 * Used as fallback when entity has no connection handles.
 * Renders a single default output handle using standard actionKey.
 */
export interface DefaultOutputHandleProps {
  entityId: string;
  entityType: 'screen' | 'block';
}

export const DefaultOutputHandle: React.FC<DefaultOutputHandleProps> = ({
  entityId,
  entityType,
}) => {
  const theme = useTheme();

  // Use different default actionKey based on entity type
  const defaultActionKey = entityType === 'screen' ? 'footer.button1' : 'action';

  // Generate proper handle ID using TransitionIdGenerator
  // Format: "handle:{entityId}:action:{actionKey}"
  const handleId = TransitionIdGenerator.generateHandleId(entityId, defaultActionKey);

  return (
    <Handle
      type="source"
      position={Position.Right}
      id={handleId}
      className="node-custom-handle node-custom-handle-source"
      data-testid={`handle-output-${entityId}-${defaultActionKey}`}
      data-handle-type="source"
      data-handle-position="right"
      data-action-key={defaultActionKey}
      data-handle-label="Btn 1"
      data-handlepos="right"
      style={{
        right: `${HANDLE_OFFSETS.OUTPUT_RIGHT}px`,
        width: `${HANDLE_DIMENSIONS.OUTPUT_WIDTH}px`,
        height: `${HANDLE_DIMENSIONS.HEIGHT}px`,
        borderRadius: `${HANDLE_BORDER_RADIUS}px`,
        background: theme.palette.background.default,
        border: `1px solid ${theme.palette.divider}`,
        color: theme.palette.text.primary,
      }}
    >
      <span style={{ fontSize: HANDLE_TEXT.OUTPUT_FONT_SIZE, lineHeight: '1' }}>
        {HANDLE_TEXT.DEFAULT_BUTTON_PREFIX} 1
      </span>
    </Handle>
  );
};
